class SlideDrawer extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.isOpen = false;
    this.focusableElements = null;
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.previousActiveElement = null;
    this.openedViaKeyboard = false;
    this._handleKeydown = this.handleKeydown.bind(this);
    this._handleEscapeKey = this.handleEscapeKey.bind(this);
    this._overlayClickHandler = this.close.bind(this);
    this._transitionEndHandler = this.handleTransitionEnd.bind(this);
    this._documentKeydownListenerAdded = false;
    this.videos = document.querySelectorAll('video');
  }

  connectedCallback() {
    // If position-mobile is not set but position is, use position as fallback
    if (!this.hasAttribute('position-mobile') && this.hasAttribute('position')) {
      const position = this.getAttribute('position');
      this.setAttribute('position-mobile', position);
    }
    
    this.render();
    this.setupEventListeners();
  }

  render() {
    const styles = `
      :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1000;
      }

      .overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        opacity: 0;
        transition: opacity 0.5s;
        will-change: opacity;
      }

      .drawer {
        position: fixed;
        background: var(--drawer-bg, #ffffff);
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        transition: all 0.5s;
        // overflow-y: auto;
        will-change: transform;
        width: var(--drawer-width, 85%);
      }

      @media (min-width: 1024px) {
        .drawer {
          width: var(--drawer-width-desktop, 500px);
        }
      }

      /* Default positions based on position attribute */
      :host([position="right"]) .drawer {
        top: 0;
        right: 0;
        height: 100%;
        transform: translateX(100%);
      }

      :host([position="left"]) .drawer {
        top: 0;
        left: 0;
        height: 100%;
        transform: translateX(-100%);
      }

      :host([position="top"]) .drawer {
        top: 0;
        left: 0;
        width: 100%;
        transform: translateY(-100%);
      }

      :host([position="bottom"]) .drawer {
        bottom: 0;
        left: 0;
        width: 100%;
        transform: translateY(100%);
      }

      /* Mobile-specific positions for smaller viewports */
      @media (max-width: 1023px) {
        :host([position-mobile="right"]) .drawer {
          top: 0;
          right: 0;
          height: 100%;
          transform: translateX(100%);
        }

        :host([position-mobile="left"]) .drawer {
          top: 0;
          left: 0;
          height: 100%;
          transform: translateX(-100%);
        }

        :host([position-mobile="top"]) .drawer {
          top: 0;
          left: 0;
          width: 100%;
          transform: translateY(-100%);
        }

        :host([position-mobile="bottom"]) .drawer {
          bottom: 0;
          left: 0;
          width: 100%;
          margin-top: calc(var(--header-height, 88px) + var(--announcement-bar-height, 0px) - 2px);
          transform: translateY(100%);
        }
      }

      :host(.open) {
        visibility: visible;
        transition: visibility 0s linear 0s;
      }

      :host(.open) .overlay {
        opacity: 1;
      }

      :host(.open) .drawer {
        transform: translate(0);
      }
    `;

    this.shadowRoot.innerHTML = `
      <style>
        ${styles}
        ::slotted(*) {
          display: block;
          // width: 100%;
          // height: 100%;
        }
      </style>
      <div class="overlay"></div>
      <div class="drawer">
        <slot></slot>
      </div>
    `;
  }

  setupEventListeners() {
    const overlay = this.shadowRoot.querySelector('.overlay');
    if (overlay) {
      overlay.removeEventListener('click', this._overlayClickHandler);
      overlay.addEventListener('click', this._overlayClickHandler);
    }

    // Handle ESC key
    if (!this._documentKeydownListenerAdded) {
      document.addEventListener('keydown', this._handleEscapeKey);
      this._documentKeydownListenerAdded = true;
    }

    // Handle transition end to properly hide the element
    const drawer = this.shadowRoot.querySelector('.drawer');
    if (drawer) {
      drawer.removeEventListener('transitionend', this._transitionEndHandler);
      drawer.addEventListener('transitionend', this._transitionEndHandler);
    }
  }

  toggleVideoPlayback(){
    this.videos.forEach(video => {
      if (this.isOpen) {
        video.pause();
      } else {
        video.play().catch((error) => {
          console.warn('Drawer: Unable to play video automatically', error);
        });
      }
    });
  }

  getFocusableElements() {
    // Get the drawer element
    const drawer = this.shadowRoot.querySelector('.drawer');
    const focusableSelectors = [
      'a[href]:not([disabled])',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([type="hidden"]):not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
      'audio[controls]',
      'video[controls]',
      'details summary',
      'iframe'
    ].join(',');

    // Get slotted elements
    const slot = this.shadowRoot.querySelector('slot');
    const slottedElements = slot ? slot.assignedElements({ flatten: true }) : [];

    // Get focusable elements from slotted content
    const slottedFocusable = slottedElements.reduce((acc, el) => {
      if (el.matches && el.matches(focusableSelectors)) {
        acc.push(el);
      }
      const children = el.querySelectorAll ? el.querySelectorAll(focusableSelectors) : [];
      acc.push(...Array.from(children));
      return acc;
    }, []);

    // Filter out any hidden elements with more comprehensive visibility check
    return slottedFocusable.filter(el => {
      // Check if element is in the DOM and has layout
      if (!el.offsetParent && el.tagName !== 'BODY') {
        // Exception for fixed/absolute positioned elements
        const style = window.getComputedStyle(el);
        if (style.position !== 'fixed' && style.position !== 'absolute') {
          return false;
        }
      }

      const style = window.getComputedStyle(el);
      if (style.display === 'none' ||
          style.visibility === 'hidden' ||
          parseFloat(style.opacity) === 0) {
        return false;
      }

      // Check if element has dimensions
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
  }

  handleKeydown(e) {
    if (!this.isOpen || !this.openedViaKeyboard) return;

    // Check if the event target is within the drawer (including slotted content)
    const isWithinDrawer = this.isElementWithinDrawer(e.target);

    if (!isWithinDrawer) {
      // If focus somehow gets outside, force it back to first element
      e.preventDefault();
      if (this.firstFocusableElement) {
        this.firstFocusableElement.focus();
      } else {
        // Fallback to the drawer itself
        this.setAttribute('tabindex', '-1');
        this.focus();
      }
      return;
    }

    if (e.key !== 'Tab') return;

    // Update focusable elements in case DOM has changed
    this.updateFocusableElements();

    // If no focusable elements, do nothing
    if (!this.focusableElements.length) return;

    const firstElement = this.firstFocusableElement;
    const lastElement = this.lastFocusableElement;
    const activeElement = document.activeElement;

    // Handle shift + tab
    if (e.shiftKey) {
      if (activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
    } 
    // Handle tab
    else {
      if (activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }

  // Helper method to check if an element is within the drawer
  isElementWithinDrawer(element) {
    if (!element) return false;

    // Check if element is in shadow DOM
    if (this.shadowRoot.contains(element)) {
      return true;
    }

    // Check if element is in slotted content
    const slot = this.shadowRoot.querySelector('slot');
    if (slot) {
      const slottedElements = slot.assignedElements({ flatten: true });
      for (const slottedElement of slottedElements) {
        if (slottedElement === element || slottedElement.contains(element)) {
          return true;
        }
      }
    }

    // Check if element is the drawer component itself
    return this === element || this.contains(element);
  }

  updateFocusableElements() {
    this.focusableElements = this.getFocusableElements();
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];

    // If no focusable elements found, make the drawer itself focusable as fallback
    if (!this.focusableElements.length) {
      const drawer = this.shadowRoot.querySelector('.drawer');
      if (drawer && !drawer.hasAttribute('tabindex')) {
        drawer.setAttribute('tabindex', '-1');
        drawer.setAttribute('aria-label', 'Drawer content');
        this.focusableElements = [drawer];
        this.firstFocusableElement = drawer;
        this.lastFocusableElement = drawer;
      }
    }
  }

  lockScroll() {
    // Save current scroll position
    this.scrollPosition = window.pageYOffset;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
  }

  unlockScroll() {
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
    
    // Temporarily disable smooth scrolling
    const htmlStyle = document.documentElement.style;
    const originalScrollBehavior = htmlStyle.scrollBehavior;
    htmlStyle.scrollBehavior = 'auto';
    
    // Restore scroll position without smooth scrolling
    window.scrollTo(0, this.scrollPosition);
    
    // Restore original scroll behavior after a small delay
    setTimeout(() => {
      htmlStyle.scrollBehavior = originalScrollBehavior;
    }, 0);
  }

  open(openedViaKeyboard = false) {
    if (this.isOpen) return;

    this.isOpen = true;
    this.openedViaKeyboard = openedViaKeyboard;
    this.style.display = 'block';
    this.offsetHeight; // Force reflow
    this.classList.add('open');
    this.lockScroll();

    // Store the currently focused element
    this.previousActiveElement = document.activeElement;

    // Only setup focus trap if opened via keyboard
    if (this.openedViaKeyboard) {
      // Add keydown listener for focus trap immediately
      document.addEventListener('keydown', this._handleKeydown, true);

      // Use requestAnimationFrame and setTimeout for reliable timing
      requestAnimationFrame(() => {
        // Add a small delay to ensure CSS transitions and DOM updates are complete
        setTimeout(() => {
          this.updateFocusableElements();

          // Focus first focusable element (which might be the drawer itself if no other elements)
          if (this.firstFocusableElement) {
            this.firstFocusableElement.focus();
          }
        }, 100); // 100ms delay should be enough for most transitions
      });
    }

    this.toggleVideoPlayback();
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.classList.remove('open');
    this.unlockScroll();

    // Remove focus trap if it was active
    if (this.openedViaKeyboard) {
      document.removeEventListener('keydown', this._handleKeydown, true);

      // Clean up any tabindex we added to the drawer itself
      const drawer = this.shadowRoot.querySelector('.drawer');
      if (drawer && drawer.getAttribute('tabindex') === '-1') {
        drawer.removeAttribute('tabindex');
        drawer.removeAttribute('aria-label');
      }

      // Remove tabindex if we added it to the component
      if (this.getAttribute('tabindex') === '-1') {
        this.removeAttribute('tabindex');
      }

      // Restore focus
      if (this.previousActiveElement && this.previousActiveElement.focus) {
        this.previousActiveElement.focus();
      }
    }

    // Reset state
    this.openedViaKeyboard = false;
    this.previousActiveElement = null;

    this.toggleVideoPlayback();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  disconnectedCallback() {
    if (this.isOpen) {
      this.unlockScroll();
    }
    document.removeEventListener('keydown', this._handleKeydown, true);
    if (this._documentKeydownListenerAdded) {
      document.removeEventListener('keydown', this._handleEscapeKey);
      this._documentKeydownListenerAdded = false;
    }
  }

  static get observedAttributes() {
    return ['position', 'position-mobile', 'class-name'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // If position changed and position-mobile doesn't exist, update position-mobile too
    if (name === 'position' && !this.hasAttribute('position-mobile')) {
      this.setAttribute('position-mobile', newValue);
    }
    
    if (name === 'position' || name === 'position-mobile' || name === 'class-name') {
      this.render();
      this.setupEventListeners();
    }
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape' && this.isOpen) {
      // Check if the event was already handled by a child component
      if (e.defaultPrevented) {
        console.debug('Drawer: ESC key event was already handled by child component');
        return;
      }
      console.debug('Drawer: Closing due to ESC key');
      this.close();
    }
  }

  handleTransitionEnd(e) {
    if (!this.isOpen && e.propertyName === 'transform') {
      this.style.display = 'none';
    }
  }
}

class DrawerOpen extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      // Check if this was triggered by keyboard (Enter or Space)
      const wasKeyboard = e.detail === 0; // detail is 0 for keyboard events

      const targetId = this.getAttribute('for');
      const drawer = document.getElementById(targetId);
      if (drawer) {
        drawer.open(wasKeyboard);
      }
    });

    // Also handle keyboard events directly
    this.addEventListener('keydown', (e) => {
      // Only respond if the event target is this element or a direct child
      // This prevents triggering when pressing Enter on form elements inside
      if (e.target !== this && !this.contains(e.target)) {
        return;
      }

      // Don't trigger if the target is a form control
      const formControls = ['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'A'];
      if (formControls.includes(e.target.tagName)) {
        return;
      }

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        const targetId = this.getAttribute('for');
        const drawer = document.getElementById(targetId);
        if (drawer) {
          drawer.open(true); // Explicitly opened via keyboard
        }
      }
    });
  }
}

class DrawerClose extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => {
      const targetId = this.getAttribute('for');
      const drawer = document.getElementById(targetId);
      if (drawer) {
        drawer.close();
      }
    });
  }
}

customElements.define('slide-drawer', SlideDrawer);
customElements.define('drawer-open', DrawerOpen);
customElements.define('drawer-close', DrawerClose);
